import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { getBatches, getServiceWithBatches, getAllSubscribedUsers } from '../services/vendorAPI';
import { cacheManager } from '../utils/cacheManager';

const CACHE_DURATION = 5000; // 5 seconds cache (reduced from 10 for fresher data)
const MAX_CACHE_SIZE = 100; // Maximum number of items in cache

const useBatch = collectionPath => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestCache = useRef(new Map());

  const cleanCache = useCallback(() => {
    const now = Date.now();
    const cache = requestCache.current;

    // Remove expired entries
    for (const [key, value] of cache.entries()) {
      if (now - value.timestamp > CACHE_DURATION) {
        cache.delete(key);
      }
    }

    // If still too many entries, remove oldest
    if (cache.size > MAX_CACHE_SIZE) {
      const entries = Array.from(cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE);
      toDelete.forEach(([key]) => cache.delete(key));
    }
  }, [collectionPath]);

  const getCached = useCallback((key) => {
    const cached = requestCache.current.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > CACHE_DURATION) {
      requestCache.current.delete(key);
      return null;
    }
    return cached.data;
  }, []);

  const setCache = useCallback((key, data) => {
    cleanCache();
    requestCache.current.set(key, {
      timestamp: Date.now(),
      data
    });
  }, [cleanCache]);

  // Register cache with cache manager on mount
  useEffect(() => {
    cacheManager.register('batchCache', requestCache.current);
    return () => {
      // Cleanup on unmount
      requestCache.current.clear();
    };
  }, []);

  const fetchBatchesByService = useCallback(async serviceId => {
    try {
      const dedupeKey = `batches-${serviceId || 'all'}`;
      const cachedResult = getCached(dedupeKey);
      if (cachedResult) {
        return cachedResult;
      }

      setLoading(true);

      let batches = [];
      if (serviceId) {
        // Get batches for specific service
        const serviceResponse = await getServiceWithBatches(serviceId);
        if (serviceResponse.success) {
          // Handle the new response structure: { data: service, pagination, vendor_id }
          // The service object has a batches array
          if (serviceResponse.data?.batches && Array.isArray(serviceResponse.data.batches)) {
            batches = serviceResponse.data.batches;
          } else if (Array.isArray(serviceResponse.data)) {
            // If data is an array (multiple services), extract batches from matching service
            const service = serviceResponse.data.find(s => s.id === parseInt(serviceId));
            batches = service?.batches || [];
          } else if (serviceResponse.data?.batches !== null && serviceResponse.data?.batches !== undefined) {
            batches = Array.isArray(serviceResponse.data.batches) ? serviceResponse.data.batches : [];
          }
        }
      } else {
        // Get all batches
        const batchesResponse = await getBatches();
        if (batchesResponse.success) {
          // Handle pagination structure if present
          batches = batchesResponse.data?.batches || batchesResponse.data || [];
        }
      }

      const result = batches.length > 0
        ? { success: true, data: batches }
        : { success: false, message: 'No batches found' };

      setCache(dedupeKey, result);
      setLoading(false);
      return result;
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError(err);
      return { success: false, error: err.message };
    }
  }, [getCached, setCache]);

  const fetchByTraineesByBatch = useCallback(async (batchId) => {
    try {
      const dedupeKey = `trainees-${batchId}`;
      const cachedResult = getCached(dedupeKey);
      if (cachedResult) {
        return cachedResult;
      }

      setLoading(true);

      // Get all subscribed users and filter by batch_id
      const response = await getAllSubscribedUsers();
      if (response.success) {
        const trainees = response.data
          .filter(sub => sub.batch_id === batchId)
          .map(sub => ({
            purchasedServiceId: sub.id,
            purchasedService: sub,
            userData: sub.customer || {},
          }));

        setCache(dedupeKey, trainees);
        setLoading(false);
        return trainees;
      }

      setLoading(false);
      return [];
    } catch (error) {
      console.error('Error fetching trainees by batch:', error);
      setLoading(false);
      setError(error);
      return [];
    }
  }, [getCached, setCache]);

  return useMemo(() => ({
    loading,
    error,
    fetchBatchesByService,
    fetchByTraineesByBatch,
  }), [loading, error, fetchBatchesByService, fetchByTraineesByBatch]);
};

export default useBatch;
